/**
 * Fetch every page of a paginated list endpoint and return the combined results.
 *
 * The API returns a paginated envelope `{ results, total }`. Views that use a list
 * to resolve references (e.g. mapping stored ids back to entities) need the COMPLETE
 * set, not just the server's default first page — otherwise references to entities
 * beyond page 1 silently fail to resolve and get dropped on save.
 *
 * Pages are requested sequentially until `total` is reached. `perPage` is only a hint:
 * if the server caps the page size, paging by `page` still walks the whole set, and an
 * empty page short-circuits the loop so a misreported `total` can't cause an infinite loop.
 *
 * @param {(params: object) => Promise<{ results: any[], total: number }>} dispatch
 *        Performs the request for a given page. Pass a bound action, e.g.
 *        `p => this.getOrganizations(p)`.
 * @param {object} [params] Base query params merged into every page request (e.g. `{ sortBy: 'name|asc' }`).
 * @param {number} [perPage] Requested page size.
 * @returns {Promise<any[]>} All results across every page.
 */
export async function fetchAll(dispatch, params = {}, perPage = 100) {
	const all = [];
	let page = 1;
	let total = Infinity;
	while (all.length < total) {
		// eslint-disable-next-line no-await-in-loop
		const res = await dispatch({ ...params, page, perPage });
		const results = (res && res.results) || [];
		total = res && typeof res.total === 'number' ? res.total : all.length + results.length;
		all.push(...results);
		if (results.length === 0) break;
		page += 1;
	}
	return all;
}

export default fetchAll;
